import React, { useCallback, useMemo } from 'react';
import styled from '@emotion/styled';
import { useState, useEffect } from 'react';
import { convertToCurrency } from '../../helpers/converters/converters';
import ContractSummaryTable from './ContractSummaryEcommTable';
import EarlyPurchaseContent from './EarlyPurchaseContent';
import MonthlyPaymentContent from '../../components/contract-summary-parts/monthly_payment';
import FineText from './FineText';
import { colors, fontFamilies } from '../../components/baseStyle/Variables';
import FormWrapper from '../../components/formWrapper/FormWrapper';
import PaymentMethod from '../../components/paymentMethod/PaymentMethod';
import { Checkbox } from '../../components/formElements/FormElements';
import Button from '../../components/button/Button';
import RadioButtonCard from '../../components/radioButtonCard/RadioButtonCard';
import { Field, Form, Formik } from 'formik';
import InternalError from '../internalError/InternalError';
import { Application, Dealer, Details, SelectLeaseOptionErrors } from '../../gql/graphql';
import { getApprovalAmount } from '../../helpers/converters/getApprovalAmount';
import { CartItemModel, ExistingApplicationEvents } from '../../state-machine/types/existingApplicationMachine.types';
import { SegmentProps } from '../../state-machine/actions/segment';
import { Role } from '../../types/role';
import { useTranslation } from 'react-i18next';
import { Spacer as ComponentSpacer } from '@koalafi/component-library';
import { EntrypointValues } from '../../constants/query-params';
import { isNonTaxInclusiveVcCompany } from '../../constants/nonTaxInclusiveVcDealers';

type ContractSummaryEcommProps = SegmentProps & {
  onNext: (arg: { type: string; data?: any }) => void;
  orderId: string;
  application: Application | undefined;
  dealer: Dealer;
  orderDetails: Details;
  selectLeaseOptionErrors?: SelectLeaseOptionErrors[];
  isLoading: boolean;
  role: Role;
  cart: CartItemModel[];
  entrypoint: string;
};

const ContractSummaryContainer = styled.div`
  margin-top: 1rem;
  font-family: ${fontFamilies.interLight};
  h2 {
    margin-bottom: 0.5rem;
  }
`;

const CheckboxWrapper = styled.div`
  cursor: pointer;
  display: flex;
  line-height: 0px !important;
  & input {
    margin-bottom: 2.4rem;
    font-size: 1rem;
  }
`;

const CheckboxLabel = styled.div`
  margin-left: 0.25rem;
  font-size: 0.7rem !important;
  line-height: 125% !important;
  color: ${colors.darkBlue};
`;

const ButtonWrap = styled.div`
  & button {
    font-family: ${fontFamilies.gilroySemiBold};
    font-size: 1rem;
    font-weight: normal;
  }
`;

const Spacer = styled.div`
  height: 1rem;
`;

type GoBackButtonProps = {
  disabled?: boolean;
};
type OptionSummaryWrapperProps = {
  multiOptions?: boolean;
};

const GoBackButton = styled.div<GoBackButtonProps>`
  color: ${(props) => (props.disabled ? colors.darkerLightGray : '#3d6edf')};
  font-weight: bold;
  cursor: pointer;
`;

const GoBackWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 1.5rem 0;
`;

const SmallSpan = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;

  white-space: pre;

  font-size: 0.875rem;
`;

const LargeSpan = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;

  white-space: pre;

  font-size: 1.25rem;
`;

const LabelWrap = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
`;

const OptionSummaryWrapper = styled.div<OptionSummaryWrapperProps>`
  box-shadow: ${(props) => (props.multiOptions ? `0px 2px 8px 0px ${colors.lightGrey}` : '')};
  border-radius: ${(props) => (props.multiOptions ? '1rem' : '')};
  padding: ${(props) => (props.multiOptions ? '0.75rem' : '')};
`;

const ContractSummaryEcomm: React.FC<ContractSummaryEcommProps> = ({
  onNext,
  isLoading,
  orderId,
  application,
  dealer,
  orderDetails,
  selectLeaseOptionErrors,
  role,
  cart,
  track,
  page,
  identify,
  entrypoint,
}) => {
  const { t } = useTranslation();
  const approvalAmount = getApprovalAmount(application);
  const virtualCardHasItems = !!cart?.length;

  const orderHasItems = !!orderDetails.items?.length;
  const areMultipleOptionsAvailable = (application?.lease?.contractSummaries?.length ?? 0) > 1;

  const initialLeaseOption = application?.lease?.contractSummaries?.reduce((min, curr) => (curr.leaseTerm < min.leaseTerm ? curr : min)).leaseTerm;

  const [agreedToLeaseTerms, setAgreedToLeaseTerms] = useState<boolean>(false);
  const [agreedToLeaseServiceTerms, setAgreedToLeaseServiceTerms] = useState<boolean>(false);
  const [selectedLeaseTerm, setSelectedLeaseTerm] = useState<string | undefined>(initialLeaseOption);

  useEffect(() => {
    page('Contract Summary', { dealerId: dealer.id, orderId });
    track('Contract Summary Viewed');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectLeaseOptionErrors?.length) {
      track('Selecting Lease Option Error');
    }
  }, [selectLeaseOptionErrors?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Need to deal with this.  Not sure what to do because we don't want the back button under the regular button?  Or do we?
  const handleUpdateCart = useCallback(
    (canEditCart: boolean) => {
      if (canEditCart) {
        onNext({ type: 'BACK' });
      }
    },
    [onNext],
  );

  const canEditCart = useMemo(() => {
    return (virtualCardHasItems || orderHasItems) && !!approvalAmount;
  }, [virtualCardHasItems, orderHasItems, approvalAmount]);

  const leaseOptionCards = useMemo(() => {
    if (!application?.lease?.contractSummaries) {
      return null;
    }

    const paymentFrequencyLabels: { [index: string]: string } = {
      weekly: t(($) => $.pageContractSummaryEcomm.optionCardFrequencyWeekly),
      biweekly: t(($) => $.pageContractSummaryEcomm.optionCardFrequencyBiweekly),
      monthly: t(($) => $.pageContractSummaryEcomm.optionCardFrequencyMonthly),
      semimonthly: t(($) => $.pageContractSummaryEcomm.optionCardFrequencySemimonthly),
    };

    return application.lease.contractSummaries
      .sort((a: { leaseTerm: string }, b: { leaseTerm: string }) => {
        return Number(a.leaseTerm) - Number(b.leaseTerm);
      })
      .map((contractSummary: any) => (
        <React.Fragment key={`${contractSummary.leaseTerm}`}>
          <Field
            id={`contractSummaryLeaseOptionSelector${contractSummary.leaseTerm}`}
            name={`contractSummaryLeaseOptionSelector`}
            radioValue={contractSummary.leaseTerm}
            radioLabel={
              <LabelWrap>
                <LargeSpan>{contractSummary.leaseTerm}</LargeSpan>
                <SmallSpan> {t(($) => $.pageContractSummaryEcomm.months)}</SmallSpan>
              </LabelWrap>
            }
            cardContent={
              <LabelWrap>
                <LargeSpan>{convertToCurrency(contractSummary.scheduledPaymentAmounts[2])}</LargeSpan>
                <SmallSpan> / {paymentFrequencyLabels[contractSummary.paymentFrequency]}</SmallSpan>
              </LabelWrap>
            }
            onChange={() => {}}
            component={RadioButtonCard}
            changeHandler={() => {
              setSelectedLeaseTerm(contractSummary.leaseTerm);
            }}
          />
        </React.Fragment>
      ));
  }, [application?.lease?.contractSummaries, t]);

  const optionSummary = useMemo(() => {
    const handleSubmit = () => {
      track('Contract Summary Acknowledged');

      onNext({ type: 'NEXT', data: selectedLeaseTerm });
    };

    // This fixes a bug where the page loads before the order query completes and thus selectedLeaseTerm is set improperly
    if (!selectedLeaseTerm && initialLeaseOption) {
      setSelectedLeaseTerm(initialLeaseOption);
    }

    if (!application?.lease?.contractSummaries || !selectedLeaseTerm) {
      return null;
    }

    const summary = application?.lease?.contractSummaries?.find((contractSummary: { leaseTerm: string | undefined }) => {
      return contractSummary.leaseTerm === selectedLeaseTerm;
    });

    if (!summary) {
      return null;
    }

    const editedPayments = [...summary.scheduledPaymentAmounts];
    const secondPayment = editedPayments?.shift();
    const finalPayment = editedPayments?.pop();
    const numberOfSimilarPayments = secondPayment === finalPayment ? summary.scheduledPaymentAmounts.length + 1 : summary.scheduledPaymentAmounts.length;

    // Item Price calculations
    let itemsTotal: number = 0.0;
    if (orderDetails?.items?.length) {
      orderDetails.items.forEach((item: { price: any; quantity: any }) => {
        itemsTotal = itemsTotal + Number(item.price) * Number(item.quantity);
      });
      itemsTotal = itemsTotal + parseFloat(orderDetails.shippingAmnt!);
    }

    const custState = application?.customer?.billingAddress?.state;

    // For Virtual Card the entered amount is already tax-inclusive, so "Total Purchase Amount"
    // shows the lease amount directly instead of re-adding tax. VC companies that are not
    // tax-inclusive (e.g. Farmers) are excluded, keyed by company dealer id. See OR-2639.
    const taxInclusive = !!dealer.isVirtualCard && !isNonTaxInclusiveVcCompany(dealer.companyDealerId);

    const paymentSummary = {
      eboDetails: {
        cashPlusFee: convertToCurrency(+summary.cashPlusFee!),
        cashPlusPrice: convertToCurrency(+summary.cashPlusPrice),
        cashPlusTerm: {
          singular: t(($) => $.pageContractSummaryEcomm.cashPlusTermSingular, { cashPlusTerm: summary.cashPlusTerm }),
          plural: t(($) => $.pageContractSummaryEcomm.cashPlusTermPlural, { cashPlusTerm: summary.cashPlusTerm }),
        },
      },
      priceDetails: {
        itemsTotal: convertToCurrency(itemsTotal),
        salesTax: convertToCurrency(+summary.tax!),
        totalAmount: convertToCurrency(taxInclusive ? +summary.leaseAmount : +summary.leaseAmount + Number(summary.tax)),
        totalWithTax: convertToCurrency(+itemsTotal + Number(summary.tax!)),
        initialPayment: convertToCurrency(+summary.initialPaymentAmount),
        costOfLease: convertToCurrency(+summary.totalCostOfOwnership - +summary.leaseAmount),
        showCostOfLease: custState === 'PA',
        costOfOwnership: convertToCurrency(+summary.totalCostOfOwnership),
        initialPaymentWithTax: convertToCurrency(+summary.initialPaymentWithTax),
      },
      paymentSchedule: {
        secondPayment: convertToCurrency(secondPayment!),
        finalPayment: convertToCurrency(finalPayment!),
        isFinalPaymentDifferent: !(secondPayment === finalPayment),
        paymentFrequency: summary.paymentFrequency,
        numSimilarPayments: numberOfSimilarPayments,
        numRemainingPayments: summary?.scheduledPaymentAmounts.length,
        timeToOwnership: `${summary?.leaseTerm} ${t(($) => $.pageContractSummaryEcomm.months)}`,
      },
    };

    return (
      <OptionSummaryWrapper multiOptions={!!areMultipleOptionsAvailable}>
        {areMultipleOptionsAvailable && (
          <>
            <h2>{t(($) => $.pageContractSummaryEcomm.contractSummary)}</h2>
            <div></div>
          </>
        )}
        <MonthlyPaymentContent
          monthlyPayment={areMultipleOptionsAvailable ? null : paymentSummary.paymentSchedule.secondPayment}
          payFrequency={paymentSummary.paymentSchedule.paymentFrequency}
        />
        <ContractSummaryTable
          isVirtualCard={!!dealer.isVirtualCard}
          retailPrice={paymentSummary.priceDetails.itemsTotal}
          tax={paymentSummary.priceDetails.salesTax}
          totalPurchaseAmount={paymentSummary.priceDetails.totalAmount}
          timeToOwnership={paymentSummary.paymentSchedule.timeToOwnership}
          paymentFrequency={paymentSummary.paymentSchedule.paymentFrequency}
          numRemainingPayments={paymentSummary.paymentSchedule.numRemainingPayments}
          initialPayment={paymentSummary.priceDetails.initialPaymentWithTax}
          secondPayment={paymentSummary.paymentSchedule.secondPayment}
          numSimilarPayments={paymentSummary.paymentSchedule.numSimilarPayments}
          finalPayment={paymentSummary.paymentSchedule.isFinalPaymentDifferent ? paymentSummary.paymentSchedule.finalPayment : null}
          costOfLease={paymentSummary.priceDetails.showCostOfLease ? paymentSummary.priceDetails.costOfLease : null}
          costOfOwnership={paymentSummary.priceDetails.costOfOwnership}
          cashPlusPrice={paymentSummary.eboDetails.cashPlusPrice}
          cashPlusTerm={paymentSummary.eboDetails.cashPlusTerm}
        />
        <EarlyPurchaseContent
          isVirtualCard={!!dealer.isVirtualCard}
          itemsWithTax={paymentSummary.priceDetails.totalWithTax}
          ipWithTax={paymentSummary.priceDetails.initialPaymentWithTax}
          cashPlusPrice={paymentSummary.eboDetails.cashPlusPrice}
          cashPlusFee={paymentSummary.eboDetails.cashPlusFee}
          cashPlusTerm={paymentSummary.eboDetails.cashPlusTerm}
          itemsTotal={paymentSummary.priceDetails.itemsTotal}
          salesTax={paymentSummary.priceDetails.salesTax}
        />
        <Spacer />
        <FineText />
        <Formik onSubmit={handleSubmit} initialValues={{}} validate={() => {}} validateOnChange={false} validateOnBlur={false} enableReinitialize={true}>
          <FormWrapper>
            <Form>
              {application?.lease?.isOtb && (
                <PaymentMethod
                  application={application}
                  onDebitEdit={() => onNext({ type: ExistingApplicationEvents.EDIT_DEBIT_INFO })}
                  track={track}
                  page={page}
                  identify={identify}
                />
              )}
              <CheckboxWrapper onClick={() => setAgreedToLeaseTerms((prev) => !prev)}>
                <Checkbox type="checkbox" name="acknowledgement" data-testid="lease-terms-checkbox" checked={agreedToLeaseTerms} onChange={() => {}} size={1} />
                <CheckboxLabel>
                  {t(($) => $.pageContractSummaryEcomm.leaseTermsAcknowledgement, {
                    firstName: application?.customer?.firstName,
                    lastName: application?.customer?.lastName,
                  })}
                </CheckboxLabel>
              </CheckboxWrapper>
              <ComponentSpacer size="lg" />
              <CheckboxWrapper onClick={() => setAgreedToLeaseServiceTerms((prev) => !prev)}>
                <Checkbox
                  type="checkbox"
                  name="acknowledgement"
                  data-testid="service-items-checkbox"
                  checked={agreedToLeaseServiceTerms}
                  onChange={() => {}}
                  size={1}
                />
                <CheckboxLabel>
                  {t(($) => $.pageContractSummaryEcomm.leaseServiceTermsAcknowledgement, {
                    firstName: application?.customer?.firstName,
                    lastName: application?.customer?.lastName,
                  })}
                </CheckboxLabel>
              </CheckboxWrapper>
              <ButtonWrap>
                <Button type="submit" disabled={!agreedToLeaseTerms || !agreedToLeaseServiceTerms} dataLoading={isLoading}>
                  {t(($) => $.pageContractSummaryEcomm.nextLeaseAgreement)}
                </Button>
              </ButtonWrap>
              {/* This shouldn't be there anymore, should be in the header */}

              {dealer.isVirtualCard && entrypoint !== EntrypointValues.MobileEmbedded && (
                <GoBackWrapper onClick={() => handleUpdateCart(canEditCart)}>
                  <GoBackButton disabled={!canEditCart}>{t(($) => $.pageContractSummaryEcomm.updateCart)}</GoBackButton>
                </GoBackWrapper>
              )}
            </Form>
          </FormWrapper>
        </Formik>
      </OptionSummaryWrapper>
    );
  }, [
    dealer.isVirtualCard,
    orderDetails,
    isLoading,
    application,
    initialLeaseOption,
    agreedToLeaseTerms,
    agreedToLeaseServiceTerms,
    selectedLeaseTerm,
    canEditCart,
    areMultipleOptionsAvailable,
    handleUpdateCart,
    onNext,
    track,
    page,
    identify,
    t,
  ]);

  return (
    <>
      <div data-testid="contract-summary-body" />
      {application?.lease?.contractSummaries && (
        <>
          {selectLeaseOptionErrors?.length ? (
            <InternalError
              orderId={orderId}
              dealer={dealer}
              role={role}
              body={t(($) => $.pageContractSummaryEcomm.unableToRecordSignature)}
              track={track}
              page={page}
              identify={identify}
            />
          ) : (
            <ContractSummaryContainer>
              <h2>
                {areMultipleOptionsAvailable
                  ? t(($) => $.pageContractSummaryEcomm.customizePaymentSchedule)
                  : t(($) => $.pageContractSummaryEcomm.heresYourContractSummary)}
              </h2>
              <div></div>
              {areMultipleOptionsAvailable ? (
                <Formik
                  onSubmit={() => {}}
                  initialValues={{
                    contractSummaryLeaseOptionSelector: selectedLeaseTerm,
                  }}
                  validate={() => {}}
                  validateOnChange={false}
                  validateOnBlur={false}
                  enableReinitialize={true}
                >
                  <Form>{leaseOptionCards}</Form>
                </Formik>
              ) : (
                <></>
              )}
              {optionSummary}
            </ContractSummaryContainer>
          )}
        </>
      )}
    </>
  );
};

export default ContractSummaryEcomm;
